import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { format } from 'date-fns'

export default function usePhysicianList() {
  // Use toast
  const toast = useToast()

  const refPhysicianListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'user', sortable: true, sortKey: 'name', label: 'Profissional',
    },
    { key: 'profession.label', label: 'Profissão', sortable: false },
    { key: 'email', sortable: true },
    { key: 'cpf', sortable: true },
    { key: 'status', sortable: true, sortKey: 'confirmed_at' },
    { key: 'institution', label: 'Instituição', sortable: false },
    { key: 'actions', label: 'Ações', sortable: false },
  ]
  const perPage = ref(25)
  const totalPhysicians = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(true)
  const situationFilter = ref(null)
  const statusFilter = ref(null)
  const classificationFilter = ref('')
  const scheduleRestrictionFilter = ref({ label: 'Todos', value: null })

  const dataMeta = computed(() => {
    const localItemsCount = refPhysicianListTable.value ? refPhysicianListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPhysicians.value,
    }
  })

  const refetchData = () => {
    refPhysicianListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, situationFilter, statusFilter, classificationFilter, scheduleRestrictionFilter], () => {
    refetchData()
  })

  const getReportParams = () => {
    let scheduleRestriction = null
    if (scheduleRestrictionFilter.value?.value) {
      scheduleRestriction = scheduleRestrictionFilter.value.value
    }
    return {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDirection: isSortDirDesc.value ? 'desc' : 'asc',
      situation: situationFilter.value,
      confirmed: statusFilter.value ? statusFilter.value.value : null,
      color: classificationFilter.value ? classificationFilter.value.value : null,
      schedule_restriction: scheduleRestriction,
    }
  }

  const fetchPhysicians = (ctx, callback) => {
    store
      .dispatch('app-physician/fetchPhysicians', getReportParams())
      .then(response => {
        const { data: physicians, meta } = response.data
        const physicianList = physicians.map(physician => {
          const p = { ...physician, status: 'active' }
          p.cpf = p.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '***.$2.$3-**')
          return p
        })
        callback(physicianList)
        totalPhysicians.value = meta.pagination.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const downloadReport = () => {
    store.dispatch('app-physician/downloadReport', getReportParams())
      .then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        const now = format(new Date(), 'yyyy-MM-dd hh:mm:ss')
        link.download = `Medicos_${now}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolvePhysicianRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolvePhysicianRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolvePhysicianStatusVariant = status => {
    if (status) return 'success'
    return 'primary'
  }

  return {
    fetchPhysicians,
    tableColumns,
    perPage,
    currentPage,
    totalPhysicians,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPhysicianListTable,

    resolvePhysicianRoleVariant,
    resolvePhysicianRoleIcon,
    resolvePhysicianStatusVariant,
    refetchData,
    downloadReport,

    // Extra Filters
    situationFilter,
    statusFilter,
    classificationFilter,
    scheduleRestrictionFilter,
  }
}
